import { Icon, IconButton, Tooltip } from '@smartgift/frontend-toolkit';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CONTACT_LIMIT_LIST, DEFAULT_CONTACT_LIMIT_LIST } from '@hero/constants';
import { useGetMerchant } from '@hero/services/merchant-service/hooks';
import { useNotification } from '@hero/store/hooks';

import { AddContactTable } from './AddContactTable';
import { RemoveContactTable } from './RemoveContactTable';
import * as S from './style';

function DoubleContactTable({
  selectedRows = {},
  disabledRows = {},
  disabledColumns = [],
  onChangeSelectedRows,
  translations = {},
  featureType = 'gift',
  isModal = false
}) {
  const { t } = useTranslation();

  const { data: merchantData } = useGetMerchant({ options: { includeConfigs: true } });
  const { showSuccess, showNotification } = useNotification();

  const contactListLimit = useMemo(() => {
    return merchantData?.configs[CONTACT_LIMIT_LIST] ?? DEFAULT_CONTACT_LIMIT_LIST;
  }, [merchantData]);

  const [addContactSelectedRows, setAddContactSelectedRows] = useState({});
  const [removeContactSelectedRows, setRemoveContactSelectedRows] = useState({});

  const selectedRowArray = useMemo(() => {
    return Object.keys(selectedRows);
  }, [selectedRows]);

  const mergedDisabledRows = useMemo(() => {
    return Object.assign({}, disabledRows, selectedRows);
  }, [selectedRows, disabledRows]);

  const removeContactSelectedRowsArray = useMemo(() => {
    return Object.keys(removeContactSelectedRows);
  }, [removeContactSelectedRows]);

  const addContactSelectedRowsArray = useMemo(() => {
    return Object.keys(addContactSelectedRows);
  }, [addContactSelectedRows]);

  const onAddContact = () => {
    if (Object.keys(selectedRows || {})?.length >= contactListLimit) {
      showNotification({
        message: t('recipientLimitMessage', { recipientLimit: contactListLimit }),
        title: t('recipientLimitTitle'),
        notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
        primaryButtonProps: {
          style: {
            margin: '0 auto'
          }
        }
      });
      return;
    }

    onChangeSelectedRows({
      ...selectedRows,
      ...addContactSelectedRows
    });

    if (!isModal)
      showSuccess({
        type: 'toast',
        message: t('typeSuccessfullyAddedToFeature', {
          count: Object.keys(addContactSelectedRows)?.length,
          type: t('recipient').toLocaleLowerCase(),
          feature: featureType
        })
      });

    setAddContactSelectedRows(() => ({}));
  };

  const onRemoveContact = () => {
    selectedRowArray.forEach(selectedRow => {
      if (removeContactSelectedRows[selectedRow]) {
        delete selectedRows[selectedRow];
      }
    });

    onChangeSelectedRows({ ...selectedRows });

    if (!isModal)
      showSuccess({
        type: 'toast',
        message: t('typeSuccessfullyRemovedFromFeature', {
          count: Object.keys(removeContactSelectedRows)?.length,
          type: t('recipient').toLocaleLowerCase(),
          feature: featureType
        })
      });

    setRemoveContactSelectedRows(() => ({}));
  };

  return (
    <S.DoubleContactTableWrapper>
      <AddContactTable
        selectedRows={addContactSelectedRows}
        onChangeSelectedRows={setAddContactSelectedRows}
        disabledRows={mergedDisabledRows}
        disabledColumns={disabledColumns}
        onNewContactUpdatingDone={contactIds => {
          const uploadedContactIds = contactIds.reduce((acc, contactId) => {
            if (!disabledRows[contactId]) acc[contactId] = true;
            return acc;
          }, {});

          setAddContactSelectedRows(addContactIds => ({ ...addContactIds, ...uploadedContactIds }));
        }}
        translations={{
          disabledRowsTooltipText: t('automationDisabledContactCheckboxTooltip', {
            featureType: t(featureType).toLocaleLowerCase()
          })
        }}
      />
      <S.NavigationButtons>
        <Tooltip
          content={t('addContactToFeature', { featureType: t(featureType).toLocaleLowerCase() })}
        >
          <S.NavigationButton>
            <IconButton
              color={addContactSelectedRowsArray.length === 0 ? 'secondary' : 'primary'}
              onClick={onAddContact}
              rounded={false}
              size="lg"
              disabled={addContactSelectedRowsArray.length === 0}
            >
              <Icon name="next" />
            </IconButton>
            {t('add')}
          </S.NavigationButton>
        </Tooltip>

        <Tooltip
          content={t('removeRecipientFromFeature', {
            featureType: t(featureType).toLocaleLowerCase()
          })}
        >
          <S.NavigationButton>
            <IconButton
              color={removeContactSelectedRowsArray.length === 0 ? 'secondary' : 'primary'}
              onClick={onRemoveContact}
              rounded={false}
              size="lg"
              disabled={removeContactSelectedRowsArray.length === 0}
            >
              <Icon name="prev" />
            </IconButton>
            {t('remove')}
          </S.NavigationButton>
        </Tooltip>
      </S.NavigationButtons>
      <RemoveContactTable
        totalContactData={selectedRowArray}
        selectedRows={removeContactSelectedRows}
        disabledColumns={disabledColumns}
        onChangeSelectedRows={setRemoveContactSelectedRows}
        translations={translations}
        featureType={featureType}
      />
    </S.DoubleContactTableWrapper>
  );
}

DoubleContactTable.S = S;

export { DoubleContactTable };
